import { useEffect, useState, useImperativeHandle, forwardRef, useRef } from 'react';
import { Box, VStack, Text, HStack, useMediaQuery, IconButton, Pressable } from 'native-base';
import { Toaster } from '../../../../components/toaster';
import { createSurveyFormAPI, editSurveyFormAPI, checkEventCoordinatorAPI } from '../../../../api/apis/workflow';
import CryptoJS from 'crypto-js';
import config from '../../../../utils/config';
import { COLORS } from '../../../../utils/colors';
import { MOBILE_VIEW_WIDTH } from '../../../../utils/screens';
import CustomButton from '../../../../components/button';
import InputField from '../../../../components/input';
import Dropdown from '../../../../components/dropdown';
import NewTextAreaField from '../../../../components/textArea/newTextArea';
import CustomModal from '../../../../components/modal';
import MobileModelSheet from '../../../../components/actionSheet';
import { LoaderAnimation } from '../../../../components/spinner';
import { deleteCardsIconSVG, addIconSVG } from '../../../../utils/svg';
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from '../../../../utils/fonts';

const SurveyFormModal = forwardRef(({ isOpen, onClose, editData, isEditing, activeTab, onSuccess }, ref) => {
  const toaster = Toaster();
  const [MobileWindow] = useMediaQuery({
    maxWidth: MOBILE_VIEW_WIDTH
  });
  const mobileSheetRef = useRef(null);

  const [surveyData, setSurveyData] = useState({
    title: '',
    type: 'event',
    event_id: '',
    questions: []
  });

  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [eventOptions, setEventOptions] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [originalQuestionValues, setOriginalQuestionValues] = useState({}); // Store original values

  // Question type options - removed radio button
  const questionTypeOptions = [
    { label: 'Input Type', value: 'input' },
    { label: 'True/False', value: 'true_false' },
    { label: 'Rating', value: 'rating' }
  ];

  useEffect(() => {
    if (isOpen && !isInitialized) {
      initializeForm();
    }
  }, [isOpen, editData]);

  // Scroll to top when modal opens
  useEffect(() => {
    if (isOpen) {
      // For mobile view, scroll the mobile sheet to top
      if (MobileWindow && mobileSheetRef.current) {
        setTimeout(() => {
          mobileSheetRef.current.scrollToTop();
        }, 100);
      }
      // For desktop view, scroll the window to top
      else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [isOpen, MobileWindow]);

  useEffect(() => {
    if (isOpen) {
      getEventList();
    }
  }, [isOpen]);

  // When events are loaded and we have edit data, set the survey type
  useEffect(() => {
    if (isOpen && editData && eventOptions.length > 0 && surveyData.event_id === '') {
      const selectedEvent = eventOptions.find(option => option.value === editData.event_id);
      if (selectedEvent) {
        setSurveyData(prev => ({
          ...prev,
          event_id: editData.event_id,
          type: selectedEvent.type
        }));
      }
    }
  }, [isOpen, editData, eventOptions, surveyData.event_id]);

  const initializeForm = () => {
    if (editData) {
      // For edit mode, prefill all data including event_id
      const questions = editData.questions?.map((q, index) => {
        // Determine question type based on options structure
        let questionType = 'input';
        
        if (q.options && q.options.length > 0) {
          // Check if it's true/false type
          if (q.options.length === 2 && 
              q.options.includes('True') && 
              q.options.includes('False')) {
            questionType = 'true_false';
          }
          // Check if it's rating type (5 options with star names)
          else if (q.options.length === 5 && 
                   q.options.every((opt, i) => opt.includes(`${i + 1} Star`) || opt.includes(`${i + 1} Stars`))) {
            questionType = 'rating';
          }
          // Otherwise it's input type
          else {
            questionType = 'input';
          }
        }
        
        // Reset options and weights based on question type
        let options = [''];
        let weights = [''];
        
        if (questionType === 'true_false') {
          options = ['True', 'False'];
          weights = [5, 4];
        } else if (questionType === 'rating') {
          options = ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'];
          weights = [1, 2, 3, 4, 5];
        } else if (questionType === 'input') {
          // For input type, preserve original options if they exist
          options = q.options && q.options.length > 0 ? q.options : [''];
          weights = q.weights && q.weights.length > 0 ? q.weights : [''];
        }
        
        return {
          question_id: parseInt(q.id || q.question_id || (index + 1)),
          question: q.question || '',
          question_type: questionType,
          options: options,
          weights: weights
        };
      }) || [];

      // Store original values for each question type
      const originalValues = {};
      editData.questions?.forEach((q, index) => {
        originalValues[index] = {
          input: {
            question: q.question || '',
            options: q.options || [''],
            weights: q.weights || ['']
          },
          true_false: {
            question: q.question || '',
            options: ['True', 'False'],
            weights: [5, 4]
          },
          rating: {
            question: q.question || '',
            options: ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'],
            weights: [1, 2, 3, 4, 5]
          }
        };
      });
      setOriginalQuestionValues(originalValues);

      setSurveyData({
        title: editData.title || '',
        type: editData.type || '',
        event_id: editData.event_id || '',
        questions: questions
      });
    } else {
      // For new survey, start with empty values and default input field
      setSurveyData({
        title: '',
        type: '', // Will be set when event is selected
        event_id: '',
        questions: [{
          question_id: 1,
          question: '',
          question_type: 'input', // Default question type
          options: [''],
          weights: [''] // Empty string for new questions
        }]
      });
    }
    setErrors({});
    setShowErrors(false);
    setIsInitialized(true);
  };

  const getEventList = async () => {
    try {
      const res = await checkEventCoordinatorAPI();
      
      if (res.error) {
        toaster(res.message, 'error', 'error');
        return;
      }

      const events = res.data?.data?.data || [];
      const options = events?.map(event => ({
        label: event.event_name,
        value: event.event_id,
        type: event.type
      }));
      setEventOptions(options);
    } catch (error) {
      toaster('Failed to load events', 'error', 'error');
    }
  };

  const updateField = (field, value) => {
    setSurveyData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const addQuestion = () => {
    setSurveyData(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        {
          question_id: prev.questions.length + 1,
          question: '',
          question_type: 'input', // Default question type
          options: [''],
          weights: [''] // Empty string for new questions
        }
      ]
    }));
  };

  const removeQuestion = (index) => {
    setSurveyData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const updateQuestion = (index, field, value) => {
    setSurveyData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i === index) {
          const updatedQuestion = { ...q, [field]: value };
          
          // Reset options based on question type
          if (field === 'question_type') {            
            // Get the original question type from editData
            let originalQuestionType = 'input';
            if (editData && editData.questions && editData.questions[index]) {
              const originalQuestion = editData.questions[index];
              if (originalQuestion.options && originalQuestion.options.length > 0) {
                if (originalQuestion.options.length === 2 && 
                    originalQuestion.options.includes('True') && 
                    originalQuestion.options.includes('False')) {
                  originalQuestionType = 'true_false';
                } else if (originalQuestion.options.length === 5 && 
                         originalQuestion.options.every((opt, i) => opt.includes(`${i + 1} Star`) || opt.includes(`${i + 1} Stars`))) {
                  originalQuestionType = 'rating';
                } else {
                  originalQuestionType = 'input';
                }
              }
            }
                        
            if (value === 'true_false') {
              if (editData && originalQuestionType === 'true_false') {
                // Preserve original true/false values
                const originalQuestion = editData.questions[index];
                updatedQuestion.question = originalQuestion.question || '';
                updatedQuestion.options = originalQuestion.options || ['True', 'False'];
                updatedQuestion.weights = originalQuestion.weights || [5, 4];
              } else {
                // Reset to initial true/false state
                updatedQuestion.question = '';
                updatedQuestion.options = ['True', 'False'];
                updatedQuestion.weights = [5, 4];
              }
            } else if (value === 'rating') {
              if (editData && originalQuestionType === 'rating') {
                // Preserve original rating values
                const originalQuestion = editData.questions[index];
                updatedQuestion.question = originalQuestion.question || '';
                updatedQuestion.options = originalQuestion.options || ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'];
                updatedQuestion.weights = originalQuestion.weights || [1, 2, 3, 4, 5];
              } else {
                // Reset to initial rating state
                updatedQuestion.question = '';
                updatedQuestion.options = ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'];
                updatedQuestion.weights = [1, 2, 3, 4, 5];
              }
            } else if (value === 'input') {
              if (editData && originalQuestionType === 'input') {
                // Preserve original input values
                const originalQuestion = editData.questions[index];
                updatedQuestion.question = originalQuestion.question || '';
                updatedQuestion.options = originalQuestion.options || [''];
                updatedQuestion.weights = originalQuestion.weights || [''];
              } else {
                // Reset to initial input state
                updatedQuestion.question = '';
                updatedQuestion.options = [''];
                updatedQuestion.weights = [''];
              }
            }
            // Force re-render
            // setTimeout(() => setRenderKey(prev => prev + 1), 0); // Removed renderKey
          }
          
          return updatedQuestion;
        }
        return q;
      })
    }));
  };

  const addOption = (questionIndex) => {
    setSurveyData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { 
              ...q, 
              options: [...q.options, ''],
              weights: [...q.weights, ''] // Empty string for new options
            }
          : q
      )
    }));
  };

  const removeOption = (questionIndex, optionIndex) => {
    setSurveyData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { 
              ...q, 
              options: q.options.filter((_, j) => j !== optionIndex),
              weights: q.weights.filter((_, j) => j !== optionIndex)
            }
          : q
      )
    }));
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    setSurveyData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { 
              ...q, 
              options: q.options.map((opt, j) => j === optionIndex ? value : opt)
            }
          : q
      )
    }));
  };

  const updateWeight = (questionIndex, optionIndex, value) => {
    setSurveyData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { 
              ...q, 
              weights: q.weights.map((weight, j) => j === optionIndex ? (value === '' ? '' : parseInt(value) || '') : weight)
            }
          : q
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!surveyData.title.trim()) {
      newErrors.title = 'Survey title is required';
    }

    if (!surveyData.event_id) {
      newErrors.event_id = 'Event selection is required';
    }

    if (!surveyData.type) {
      newErrors.type = 'Survey type is required (select an event)';
    }

    if (surveyData.questions.length === 0) {
      newErrors.questions = 'At least one question is required';
    }

    // Check if all questions are empty (user might have deleted content from default question)
    const hasValidQuestions = surveyData.questions.some(q => q.question.trim() !== '');
    if (!hasValidQuestions) {
      newErrors.questions = 'At least one question with content is required';
    }

    surveyData.questions.forEach((question, qIndex) => {
      if (!question.question.trim()) {
        newErrors[`question_${qIndex}`] = 'Question text is required';
      }
      
      if (question.options.length === 0 || question.options.some(opt => !opt.trim())) {
        newErrors[`options_${qIndex}`] = 'All options must be filled';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    const isValid = validateForm();
    setShowErrors(true);

    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    LoaderAnimation.show();

    try {
      const payload = {
        title: surveyData.title,
        type: surveyData.type,
        event_id: surveyData.event_id,
        questions: surveyData.questions.map(q => ({
          question_id: parseInt(q.question_id),
          question: q.question,
          options: q.options,
          weights: q.weights
        }))
      };

      let response;
      if (editData) {
        response = await editSurveyFormAPI(editData._id, payload);
      } else {
        response = await createSurveyFormAPI(payload);
      }

      if (response.error) {
        toaster(response.message || `Failed to ${editData ? 'update' : 'create'} survey form`, 'error', 'error');
      } else {
        toaster(`Survey form ${editData ? 'updated' : 'created'} successfully`, 'success', 'success');
        if (onSuccess) {
          handleClose();
          setTimeout(() => {
            onSuccess();
          }, 100);
        } else {
          handleClose();
        }
      }
    } catch (error) {
      console.error(`Error ${editData ? 'updating' : 'creating'} survey form:`, error);
      toaster(`Error ${editData ? 'updating' : 'creating'} survey form`, 'error', 'error');
    } finally {
      setIsSubmitting(false);
      LoaderAnimation.hide();
    }
  };

  const handleClose = () => {
    setSurveyData({
      title: '',
      type: '',
      event_id: '',
      questions: []
    });
    setErrors({});
    setShowErrors(false);
    setIsInitialized(false);
    onClose();
  };

  useImperativeHandle(ref, () => ({
    handleSubmit
  }));

  const renderStarRating = (question, qIndex) => {
    const maxStars = 5;
    const stars = [];
    
    for (let i = 1; i <= maxStars; i++) {
      stars.push(
        <Pressable
          key={i}
          onPress={() => {
            // Update the option text and weight based on selected star
            const newOptions = [];
            const newWeights = [];
            
            for (let j = 1; j <= maxStars; j++) {
              if (j <= i) {
                newOptions.push(`${j} Star${j > 1 ? 's' : ''}`);
                // For rating questions, the weight should be the maximum value (5) when user selects the max stars
                // So if admin selects 4 stars, when user selects 4 stars, weight should be 5
                newWeights.push(j);
              }
            }
            
            setSurveyData(prev => ({
              ...prev,
              questions: prev.questions.map((q, idx) => 
                idx === qIndex 
                  ? { 
                      ...q, 
                      options: newOptions,
                      weights: newWeights
                    }
                  : q
              )
            }));
          }}
        >
          <Text
            fontSize="2xl"
            color={question.options.length >= i ? "yellow.400" : "gray.300"}
            mr={1}
          >
            ★
          </Text>
        </Pressable>
      );
    }
    
    return (
      <VStack space={2}>
        <Text fontSize="md" color="gray.600" fontWeight="medium">
          Rating Scale (Max {question.options.length} stars):
        </Text>
        <HStack space={1} alignItems="center">
          {stars}
          <Text fontSize="sm" color="gray.500" ml={2}>
            ({question.options.length} stars selected)
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.500" italic>
          Note: When users select the maximum stars, they will get weight 5
        </Text>
      </VStack>
    );
  };

  const renderQuestionOptions = (question, qIndex) => {
    if (question.question_type === 'rating') {
      return renderStarRating(question, qIndex);
    }

    return (
      <Box key={`options-${qIndex}-${question.question_type}`}>
        <HStack justifyContent="space-between" alignItems="center" mb={2}>
          <Text fontWeight="medium">Options</Text>
          {question.question_type === 'input' && (
            <CustomButton
              text="Add Option"
              onPress={() => addOption(qIndex)}
              size="md"
              isOutline
              textColor={COLORS.THEME_COLOR}
            />
          )}
        </HStack>

        {showErrors && errors[`options_${qIndex}`] && (
          <Text color="red.500" fontSize="sm" mb={2}>{errors[`options_${qIndex}`]}</Text>
        )}

        <VStack space={2}>
          {question.options.map((option, oIndex) => (
            <HStack key={`option-${qIndex}-${oIndex}`} space={2} alignItems="center">
              <Box flex={1}>
                <InputField
                  label={`Option ${oIndex + 1}`}
                  value={option}
                  onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                  placeholder={`Enter option ${oIndex + 1}`}
                />
              </Box>
              <Box w="100px">
                <InputField
                  label="Weight"
                  value={question.weights[oIndex] !== undefined && question.weights[oIndex] !== '' ? question.weights[oIndex] : ''}
                  onChange={(e) => updateWeight(qIndex, oIndex, e.target.value)}
                  keyboardType="numeric"
                  placeholder="5"
                />
              </Box>
              {question.options.length > 1 && question.question_type === 'input' && (
                <IconButton
                  icon={deleteCardsIconSVG}
                  onPress={() => removeOption(qIndex, oIndex)}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  mt={6}
                  opacity={1}
                  _hover={{ bg: 'red.100' }}
                  _pressed={{ bg: 'red.200' }}
                />
              )}
            </HStack>
          ))}
        </VStack>
      </Box>
    );
  };

  const modalBody = () => (
    <Box px={1}>
      <VStack space={4}>
        {/* Survey Title */}
        <InputField
          label="Survey Title *"
          value={surveyData.title}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder="Enter survey title"
          isInvalid={showErrors && !!errors.title}
          errorMessage={showErrors ? errors.title : ''}
        />

        {/* Event Selection */}
        <Box>
          <Dropdown
            key={`event-dropdown-${surveyData.event_id}`}
            label="Select Event *"
            selectedValues={surveyData.event_id}
            setSelectedValues={(value) => {
              // Find the selected event to get its type
              const selectedEvent = eventOptions.find(option => option.value === value);
              if (selectedEvent) {
                // Update both event_id and survey type
                setSurveyData(prev => ({
                  ...prev,
                  event_id: value,
                  type: selectedEvent.type
                }));
              } else {
                updateField('event_id', value);
              }
            }}
            dropItems={eventOptions}
            placeholder="an event"
            isInvalid={showErrors && !!errors.event_id}
            errorMessage={showErrors ? errors.event_id : ''}
          />

        </Box>

        {/* Survey Type - Read Only */}
        <InputField
          key={`survey-type-${surveyData.type}`}
          label="Survey Type *"
          value={surveyData.type ? (surveyData.type === 'event' ? 'Event' : 'MICE') : 'Select an event to set type'}
          isReadOnly={true}
          bg="gray.100"
          isDisabled={true}
        />

        {/* Questions Section */}
        <Box>
          <HStack justifyContent="space-between" alignItems="center" mb={3}>
            <Text fontSize="lg" fontWeight="semibold">Questions</Text>
            <CustomButton
              text="Add Question"
              onPress={addQuestion}
              size="sm"
              startIcon={addIconSVG}
            />
          </HStack>

          {showErrors && errors.questions && (
            <Text color="red.500" fontSize="sm" mb={2}>{errors.questions}</Text>
          )}

          <VStack space={4}>
            {surveyData.questions.map((question, qIndex) => (
              <Box key={qIndex} p={4} borderWidth={1} borderColor="gray.200" borderRadius="md">
                <HStack justifyContent="space-between" alignItems="center" mb={3}>
                  <Text fontWeight="medium">Question {qIndex + 1}</Text>
                  <IconButton
                    icon={deleteCardsIconSVG}
                    onPress={() => removeQuestion(qIndex)}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    opacity={1}
                    _hover={{ bg: 'red.100' }}
                    _pressed={{ bg: 'red.200' }}
                  />
                </HStack>

                {/* Question Type Selection */}
                <Box mb={3}>
                  <Dropdown
                    key={`question-type-${qIndex}-${question.question_type}`}
                    label="Question Type *"
                    selectedValues={question.question_type}
                    setSelectedValues={(value) => updateQuestion(qIndex, 'question_type', value)}
                    dropItems={questionTypeOptions}
                    placeholder="Select question type"
                  />
                </Box>

                {/* Question Text */}
                <NewTextAreaField
                  label="Question Text *"
                  value={question.question}
                  onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                  placeholder="Enter your question"
                  isInvalid={showErrors && !!errors[`question_${qIndex}`]}
                  errorMessage={showErrors ? errors[`question_${qIndex}`] : ''}
                />

                {/* Options Section */}
                <Box mt={4}>
                  {renderQuestionOptions(question, qIndex)}
                </Box>
              </Box>
            ))}
          </VStack>
        </Box>
      </VStack>
    </Box>
  );

  const modalFooter = () => (
    <HStack space={4} justifyContent="flex-end">
      <CustomButton
        text="Cancel"
        onPress={handleClose}
        variant="outline"
        bgColor='transparent'
        textColor={COLORS.MODAL_BACK_BUTTON_COLOR}
      />
      <CustomButton
        text={isSubmitting ? (editData ? "Updating..." : "Creating...") : (editData ? "Update Survey" : "Create Survey")}
        onPress={handleSubmit}
        disabled={isSubmitting}
      />
    </HStack>
  );

  if (!isOpen || !isInitialized) {
    return null;
  }

  return MobileWindow ? (
    <MobileModelSheet
      ref={mobileSheetRef}
      isOpen={isOpen}
      onClose={handleClose}
      header={editData ? "Edit Survey Form" : "Create Survey Form"}
      body={modalBody()}
      footer={modalFooter()}
      maxH="85vh"
    />
  ) : (
    <CustomModal
      isOpen={isOpen}
      onClose={handleClose}
      header={editData ? "Edit Survey Form" : "Create Survey Form"}
      body={modalBody()}
      footer={modalFooter()}
      width="60%"
      maxH="80vh"
    />
  );
});

SurveyFormModal.displayName = 'SurveyFormModal';

export default SurveyFormModal; 